import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Activity, AlertCircle } from "lucide-react";

interface SystemDetailsModalProps {
  open: boolean;
  onClose: () => void;
  system: {
    name: string;
    status: "excellent" | "good" | "attention" | "critical";
    metrics: number;
    alerts: number;
    summary?: string;
    detailed_metrics?: Array<{
      name: string;
      value: string | number;
      unit?: string;
      status: "normal" | "abnormal";
      reference?: string;
    }>;
  };
}

export function SystemDetailsModal({ open, onClose, system }: SystemDetailsModalProps) {
  const statusConfig = {
    excellent: { color: "bg-emerald-500", label: "Excelente", textColor: "text-emerald-700" },
    good: { color: "bg-blue-500", label: "Bom", textColor: "text-blue-700" },
    attention: { color: "bg-amber-500", label: "Atenção", textColor: "text-amber-700" },
    critical: { color: "bg-red-500", label: "Crítico", textColor: "text-red-700" }
  };

  const config = statusConfig[system.status];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 capitalize">
            <Activity className={`h-5 w-5 ${config.textColor}`} />
            Sistema {system.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status Geral */}
          <Card className={`border-l-4 border-l-${config.color}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Badge className={config.color} variant="default">
                    {config.label}
                  </Badge>
                </div>
                <div className="text-right text-sm text-muted-foreground">
                  <p>{system.metrics} métricas analisadas</p>
                  {system.alerts > 0 && (
                    <p className="text-red-600 font-semibold flex items-center gap-1 justify-end">
                      <AlertCircle className="h-4 w-4" />
                      {system.alerts} alertas
                    </p>
                  )}
                </div>
              </div>

              {system.summary && (
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {system.summary}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Métricas Detalhadas */}
          {system.detailed_metrics && system.detailed_metrics.length > 0 ? (
            <div>
              <h3 className="font-semibold mb-3 text-sm">Métricas Envolvidas</h3>
              <div className="space-y-2">
                {system.detailed_metrics.map((metric, index) => (
                  <Card 
                    key={index}
                    className={metric.status === "abnormal" ? "border-red-200 bg-red-50/50" : ""}
                  >
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-medium text-sm">{metric.name}</h4>
                            {metric.status === "abnormal" && (
                              <Badge variant="destructive" className="text-xs">
                                Fora da faixa
                              </Badge>
                            )}
                          </div>
                          {metric.reference && (
                            <p className="text-xs text-muted-foreground">
                              Referência: {metric.reference}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <div className={`text-lg font-bold ${
                            metric.status === "abnormal" ? "text-red-600" : "text-primary"
                          }`}>
                            {metric.value}
                            {metric.unit && (
                              <span className="text-sm text-muted-foreground ml-1">
                                {metric.unit}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-4">
                <p className="text-sm text-amber-700 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    Nenhuma métrica específica foi encontrada para este sistema nos registros atuais. 
                    Adicione mais exames laboratoriais relacionados para visualizar detalhes.
                  </span>
                </p>
              </CardContent>
            </Card>
          )}

          {/* Informação adicional */}
          <Card className="bg-muted/30">
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground">
                <strong>Nota:</strong> As métricas exibidas são baseadas nos registros de saúde 
                mais recentes do paciente. Para análise completa, consulte o histórico detalhado 
                de exames e resultados.
              </p>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
